import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import Link from '@medflex/components/stories/Link';
import { AutoResponseMessage } from '@medflex/shared-constants';
import { Switch } from '@medflex/components/stories/Switch';
import { trackCustomEvent } from '@medflex/utils/Tracking';
import { UserTypes } from '@medflex/shared-types';
import useModal from '../../hooks/useModal';
import { MODALS } from '../../hooks/useIsModal';
import useGreetingMessage from '../../hooks/useGreetingMessage';
import useAutoResponseMessage from '../../hooks/useAutoResponseMessage';
import useUserProfile from '../../hooks/useUserProfile';

const ChatSettingsModule = () => {
  const { t } = useTranslation('physician', { keyPrefix: 'ChatSettingsModule' });
  const { open } = useModal();
  const { greetingMessage } = useGreetingMessage();
  const { autoResponseMessage } = useAutoResponseMessage();
  const { userProfile, editContactPage, updateChatSettings } = useUserProfile();
  const anonymousUserAllowed = Boolean(
    userProfile?.attributes?.pages?.inquiryManagementSettings?.anonymousUser?.allowedAt,
  );
  const patientChatAllowed = Boolean(
    userProfile?.attributes?.chatSettings?.[UserTypes.PATIENT].canSendMessages,
  );

  const handleAnonymousUserSwitchChange = useCallback(
    checked => {
      editContactPage({
        inquiryManagementSettings: {
          anonymousUser: {
            allowed: checked || null,
          },
        },
      });

      trackCustomEvent({
        eventCategory: 'physician_configuration',
        eventAction: 'switch | toggled',
        eventName: `anonyMouseUser option ${checked ? 'activated' : 'deactivated'}`,
      });
    },
    [editContactPage],
  );

  const handlePatientChatSwitchChange = useCallback(
    checked => {
      updateChatSettings({
        [UserTypes.PATIENT]: {
          canSendMessages: checked || null,
        },
      });

      trackCustomEvent({
        eventCategory: 'physician_configuration',
        eventAction: 'switch | toggled',
        eventName: `patientCanSendMessages option ${checked ? 'activated' : 'deactivated'}`,
      });
    },
    [updateChatSettings],
  );

  return (
    <div data-component="ChatSettingsModule">
      <header className="mb-6 flex items-center">
        <h2 className="text-2xl font-semibold">{t('title')}</h2>
      </header>
      <p className="mb-6">{t('description')}</p>
      <div className="flex flex-col space-y-6">
        <SettingsBox
          title={t('greeting')}
          canEdit
          truncate
          onEdit={() => open({ modal: MODALS.greetingMessageEdit })}
        >
          <p>{t('introduction')}</p>
          <p className="truncate">{greetingMessage || t('defaultGreeting')}</p>
        </SettingsBox>
        <SettingsBox
          title={t('autoResponse.title')}
          canEdit
          truncate
          onEdit={() => open({ modal: MODALS.autoResponseMessageEdit })}
        >
          <p className="truncate">{autoResponseMessage || AutoResponseMessage.DEFAULT}</p>
        </SettingsBox>
        <SettingsBox title={t('inquiryManagement.title')}>
          <div className="mb-3">{t('inquiryManagement.description')}</div>
          <Link kind="button:outline" to="/settings/inquiryManagement">
            {t('inquiryManagement.link')}
          </Link>
        </SettingsBox>
        <SettingsBox title={t('inquiryManagement.anonymous_user.title')}>
          <label
            className="flex justify-between mb-3"
            htmlFor="ChatSettingsModule_inquiryManagement_anonymousCommunicationSwitch"
          >
            <div className="p-2 pl-0 font-semibold">
              {t('inquiryManagement.anonymous_user.switch_label')}
            </div>
            <Switch
              id="ChatSettingsModule_inquiryManagement_anonymousCommunicationSwitch"
              checked={anonymousUserAllowed}
              label={t('inquiryManagement.anonymous_user.switch_label')}
              onChange={handleAnonymousUserSwitchChange}
            />
          </label>
          <Trans t={t} i18nKey="inquiryManagement.anonymous_user.description" parent="div">
            This feature enables written communication with contacts who are not registered with
            medflex.{' '}
            <a
              href="https://medflex.de/hilfe/behandler-neue-funktionen/kommunikation-mit-nicht-registrierten-kontakten"
              target="_blank"
              className="hover:underline text-blue-500"
              rel="noreferrer"
            >
              Read more about it here
            </a>
            . Important: Mention the use of medflex in your data protection notices.
          </Trans>
        </SettingsBox>
        <SettingsBox title={t('contactChat.patient.canSendMessages.title')}>
          <label className="flex justify-between mb-3" htmlFor="patientChatSwitch">
            <div className="p-2 pl-0 font-semibold">
              {t('contactChat.patient.canSendMessages.switch_label')}
            </div>
            <Switch
              id="ChatSettingsModule_contactChat_patientChatSwitch"
              checked={patientChatAllowed}
              label={t('contactChat.patient.canSendMessages.switch_label')}
              onChange={handlePatientChatSwitchChange}
            />
          </label>
          <div>{t('contactChat.patient.canSendMessages.description')}</div>
        </SettingsBox>
      </div>
    </div>
  );
};

export default ChatSettingsModule;
